import {Component, EventEmitter, Input, Output} from '@angular/core';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {EntryReference, FolderItemType, FolderItemTypeInfo} from '../../../dtos/folder/folder';
import {FormOptionsLists} from '../../../shared/FormOptionsLists';
import {WordService} from '../../../services/vocab/word.service';
import {FuncGrammarService} from '../../../services/grammar/func-grammar.service';
import {SysGrammarService} from '../../../services/grammar/sys-grammar.service';
import {GlobalCanDoStatementService} from '../../../services/can-do-statement/global-can-do-statement.service';
import {DetailedCanDoStatementService} from '../../../services/can-do-statement/detailed-can-do-statement.service';
import {VerbalActionEntryService} from '../../../services/verbal-action/verbal-action-entry.service';
import {TextTypeService} from '../../../services/text/text-type.service';
import {MatDialog} from '@angular/material/dialog';
import {AuthService} from '../../../services/auth/auth.service';
import {NewWordComponent} from '../../vocabulary/create/new-word/new-word.component';
import {NewGrammarEntryComponent} from '../../grammar/new-grammar-entry/new-grammar-entry.component';
import {NewCanDoStatementComponent} from '../../can-do-statement/create/new-can-do-statement/new-can-do-statement.component';
import {NewVerbalActionComponent} from '../../verbal-action/create/new-verbal-action/new-verbal-action.component';
import {NewTextTypeComponent} from '../../text-type/new-text-type/new-text-type.component';

/** edit dialogs of the modules, text patterns are edited on their own page */
const EDIT_DIALOGS: { [type in FolderItemType]?: any } = {
  VOCAB_ENTRY: NewWordComponent,
  FUNCTIONAL_GRAMMAR_ENTRY: NewGrammarEntryComponent,
  SYSTEMIC_GRAMMAR_ENTRY: NewGrammarEntryComponent,
  GLOBAL_CDS_ENTRY: NewCanDoStatementComponent,
  DETAILED_CDS_ENTRY: NewCanDoStatementComponent,
  VERBAL_ACTION_ENTRY: NewVerbalActionComponent,
  TEXT_TYPE: NewTextTypeComponent,
};

const COLUMNS: { [type in FolderItemType]: string[] } = {
  VOCAB_ENTRY: ['productive', 'receptive', 'name', 'country', 'category'],
  FUNCTIONAL_GRAMMAR_ENTRY: ['productive', 'receptive', 'name', 'example', 'category'],
  SYSTEMIC_GRAMMAR_ENTRY: ['productive', 'receptive', 'name', 'example', 'category'],
  GLOBAL_CDS_ENTRY: ['receptive', 'productive', 'name', 'mode', 'activity', 'category'],
  DETAILED_CDS_ENTRY: ['receptive', 'productive', 'name', 'mode', 'activity', 'category'],
  VERBAL_ACTION_ENTRY: ['receptive', 'productive', 'name', 'category'],
  TEXT_TYPE: ['name'],
  TEXT_PATTERN: ['name'],
};

/**
 * the entries of one type in a Sammelmappe, with optional checkboxes and the details of an entry
 */
@Component({
  selector: 'app-folder-entry-table',
  templateUrl: './folder-entry-table.component.html',
  styleUrls: ['./folder-entry-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: false
})
export class FolderEntryTableComponent {

  @Input() typeInfo: FolderItemTypeInfo;
  @Input() entries: EntryReference[] = [];
  @Input() selectable = false;
  /** ids of the selected entries, changed in place */
  @Input() selection = new Set<number>();
  @Output() selectionChange = new EventEmitter<Set<number>>();
  /** an admin saved changes to an entry */
  @Output() entryEdited = new EventEmitter<void>();

  expanded: EntryReference = null;
  formOptionsLists = new FormOptionsLists();

  constructor(private wordService: WordService,
              private funcGrammarService: FuncGrammarService,
              private sysGrammarService: SysGrammarService,
              private globalCdsService: GlobalCanDoStatementService,
              private detailedCdsService: DetailedCanDoStatementService,
              private verbalActionEntryService: VerbalActionEntryService,
              private textTypeService: TextTypeService,
              private dialog: MatDialog,
              private authService: AuthService) {
  }

  get displayedColumns(): string[] {
    const columns = [...COLUMNS[this.typeInfo.type], ...(this.canEditEntries ? ['edit'] : []), 'details'];
    return this.selectable ? ['select', ...columns] : columns;
  }

  /** admins can edit the entries themselves, like in the modules */
  get canEditEntries(): boolean {
    return !!EDIT_DIALOGS[this.typeInfo.type] && this.authService.getUserRole() === 'ROLE_ADMIN';
  }

  editEntry(entry: EntryReference): void {
    this.dialog.open(EDIT_DIALOGS[this.typeInfo.type], {data: {id: entry.id, service: this.detailService}})
      .afterClosed()
      .subscribe(saved => {
        if (saved) {
          // the details may show the old version otherwise
          this.expanded = null;
          this.entryEdited.emit();
        }
      });
  }

  /**
   * service for the entry details, text patterns have their own page instead
   */
  get detailService() {
    switch (this.typeInfo.type) {
      case 'VOCAB_ENTRY':
        return this.wordService;
      case 'FUNCTIONAL_GRAMMAR_ENTRY':
        return this.funcGrammarService;
      case 'SYSTEMIC_GRAMMAR_ENTRY':
        return this.sysGrammarService;
      case 'GLOBAL_CDS_ENTRY':
        return this.globalCdsService;
      case 'DETAILED_CDS_ENTRY':
        return this.detailedCdsService;
      case 'VERBAL_ACTION_ENTRY':
        return this.verbalActionEntryService;
      case 'TEXT_TYPE':
        return this.textTypeService;
      default:
        return null;
    }
  }

  get nameHeader(): string {
    return this.typeInfo.type === 'VOCAB_ENTRY' ? 'Wort' : 'Eintrag';
  }

  isAllSelected(): boolean {
    return this.entries.length > 0 && this.entries.every(e => this.selection.has(e.id));
  }

  isSomeSelected(): boolean {
    return this.entries.some(e => this.selection.has(e.id)) && !this.isAllSelected();
  }

  toggleAll(checked: boolean): void {
    this.entries.forEach(e => checked ? this.selection.add(e.id) : this.selection.delete(e.id));
    this.selectionChange.emit(this.selection);
  }

  toggle(entry: EntryReference, checked: boolean): void {
    if (checked) {
      this.selection.add(entry.id);
    } else {
      this.selection.delete(entry.id);
    }
    this.selectionChange.emit(this.selection);
  }

  toggleDetails(entry: EntryReference): void {
    this.expanded = this.expanded === entry ? null : entry;
  }

  level(level: string): string {
    return level === 'NONE' ? 'Offen' : level;
  }

  country(country: string): string {
    return this.formOptionsLists.countryToString.get(country) ?? country ?? '';
  }

  mode(mode: string): string {
    return this.formOptionsLists.modesToString.get(mode) ?? mode ?? '';
  }

  activity(activity: string): string {
    return this.formOptionsLists.activityToString.get(activity) ?? activity ?? '';
  }
}
